import { create } from 'zustand'

/**
 * Lightweight store to pass the initial prompt from HomePage to SideChat.
 * When a user types a prompt on the home page and creates a creative,
 * the prompt is stored here so SideChat can pick it up and send it
 * as the first real message via the AI SDK.
 */
export interface InitialAttachment {
  attachmentId: string
  dataUrl: string
  mediaType: string
}

interface InitialPromptStore {
  prompts: Record<string, string>
  attachments: Record<string, InitialAttachment>
  setPrompt: (creativeId: string, prompt: string) => void
  consumePrompt: (creativeId: string) => string | undefined
  setAttachment: (creativeId: string, attachment: InitialAttachment) => void
  consumeAttachment: (creativeId: string) => InitialAttachment | undefined
}

export const useInitialPromptStore = create<InitialPromptStore>((set, get) => ({
  prompts: {},
  attachments: {},

  setPrompt: (creativeId, prompt) => {
    set(state => ({
      prompts: { ...state.prompts, [creativeId]: prompt },
    }))
  },

  consumePrompt: creativeId => {
    const prompt = get().prompts[creativeId]
    if (prompt) {
      set(state => {
        const { [creativeId]: _, ...rest } = state.prompts
        return { prompts: rest }
      })
    }
    return prompt
  },

  setAttachment: (creativeId, attachment) => {
    set(state => ({
      attachments: { ...state.attachments, [creativeId]: attachment },
    }))
  },

  consumeAttachment: creativeId => {
    const attachment = get().attachments[creativeId]
    if (attachment) {
      set(state => {
        const { [creativeId]: _, ...rest } = state.attachments
        return { attachments: rest }
      })
    }
    return attachment
  },
}))

/**
 * Repeatable prompt queue for triggering AI messages from outside SideChat.
 * Unlike useInitialPromptStore (which fires once on mount), this store is
 * consumed reactively so prompts can be queued at any time (e.g. from a
 * "Generate" button in the mobile toolbar).
 */
interface QueuedPromptStore {
  prompts: Record<string, string>
  setPrompt: (creativeId: string, prompt: string) => void
  consumePrompt: (creativeId: string) => string | undefined
}

export const useQueuedPromptStore = create<QueuedPromptStore>((set, get) => ({
  prompts: {},

  setPrompt: (creativeId, prompt) => {
    set(state => ({
      prompts: { ...state.prompts, [creativeId]: prompt },
    }))
  },

  consumePrompt: creativeId => {
    const prompt = get().prompts[creativeId]
    if (prompt) {
      set(state => {
        const { [creativeId]: _, ...rest } = state.prompts
        return { prompts: rest }
      })
    }
    return prompt
  },
}))

/**
 * Prefill the chat input text without sending. Used by the preview
 * "Change..." actions to open the chat with a pre-filled editing prompt
 * that the user can complete and send.
 */
interface ChatPrefillStore {
  prefills: Record<string, string>
  setPrefill: (creativeId: string, text: string) => void
  consumePrefill: (creativeId: string) => string | undefined
}

export const useChatPrefillStore = create<ChatPrefillStore>((set, get) => ({
  prefills: {},

  setPrefill: (creativeId, text) => {
    set(state => ({
      prefills: { ...state.prefills, [creativeId]: text },
    }))
  },

  consumePrefill: creativeId => {
    const text = get().prefills[creativeId]
    if (text) {
      set(state => {
        const { [creativeId]: _, ...rest } = state.prefills
        return { prefills: rest }
      })
    }
    return text
  },
}))

/**
 * Section context for the "Ask AI" flow.
 * When a user clicks the chat button on a hovered preview section,
 * the section reference is stored here so SideChat can render a
 * context chip and include the section data with the next message.
 * Works across creative types (email sections, Instagram fields, etc.).
 */
export interface SectionContext {
  sectionId: string
  sectionType: string
  label: string
  preview: string
}

interface SectionContextStore {
  contexts: Record<string, SectionContext>
  setContext: (creativeId: string, context: SectionContext) => void
  clearContext: (creativeId: string) => void
  consumeContext: (creativeId: string) => SectionContext | undefined
}

export const useSectionContextStore = create<SectionContextStore>((set, get) => ({
  contexts: {},

  setContext: (creativeId, context) => {
    set(state => ({
      contexts: { ...state.contexts, [creativeId]: context },
    }))
  },

  clearContext: creativeId => {
    set(state => {
      const { [creativeId]: _, ...rest } = state.contexts
      return { contexts: rest }
    })
  },

  consumeContext: creativeId => {
    const ctx = get().contexts[creativeId]
    if (ctx) {
      set(state => {
        const { [creativeId]: _, ...rest } = state.contexts
        return { contexts: rest }
      })
    }
    return ctx
  },
}))

/**
 * Requests the editor to scroll-to and highlight a specific section.
 * Used by the "Show in Editor" button on the email preview hover overlay.
 */
interface EditorFocusStore {
  focusRequests: Record<string, string>
  requestFocus: (creativeId: string, sectionId: string) => void
  consumeFocus: (creativeId: string) => string | undefined
}

export const useEditorFocusStore = create<EditorFocusStore>((set, get) => ({
  focusRequests: {},

  requestFocus: (creativeId, sectionId) => {
    set(state => ({
      focusRequests: { ...state.focusRequests, [creativeId]: sectionId },
    }))
  },

  consumeFocus: creativeId => {
    const sectionId = get().focusRequests[creativeId]
    if (sectionId) {
      set(state => {
        const { [creativeId]: _, ...rest } = state.focusRequests
        return { focusRequests: rest }
      })
    }
    return sectionId
  },
}))

/**
 * Tracks which section is currently being edited (has input focus) in the
 * editor panel, so the preview can show a subtle ring indicator.
 */
interface ActiveEditingSectionStore {
  activeSections: Record<string, string | null>
  setActive: (creativeId: string, sectionId: string) => void
  clearActive: (creativeId: string) => void
}

export const useActiveEditingSectionStore = create<ActiveEditingSectionStore>(set => ({
  activeSections: {},

  setActive: (creativeId, sectionId) => {
    set(state => ({
      activeSections: { ...state.activeSections, [creativeId]: sectionId },
    }))
  },

  clearActive: creativeId => {
    set(state => ({
      activeSections: { ...state.activeSections, [creativeId]: null },
    }))
  },
}))
